import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import z from "zod";

import { YesOrNoBadge } from "@/components/profiles";
import { QueryBuilderContext } from "@/contexts/query-builder-context";
import { useIntegrations } from "@/hooks/use-integrations";
import { useTableQueryBuilder } from "@/hooks/use-table-query-builder";
import type { BayesiaResult } from "@/types/integrations";
import { PaginatedTable } from "@/ui/common/paginated-table";
import { HeaderSearchInput } from "@/ui/table/header-search-input";
import { SearchHeaderSelect } from "@/ui/table/search-header-select";
import { formatShortDate, YES_OR_NO_OPTIONS } from "@/utils";

const columns: ColumnDef<BayesiaResult>[] = [
  {
    id: "id",
    accessorKey: "id",
  },
  {
    id: "userId",
    accessorKey: "userId",
    header: (header) => {
      return <HeaderSearchInput header={header} name="Profile ID" />;
    },
    enableSorting: false,
  },
  {
    id: "model",
    accessorKey: "model",
    header: (header) => {
      return <HeaderSearchInput header={header} name="model" />;
    },
  },
  {
    id: "modelVersion",
    header: (header) => {
      return <HeaderSearchInput header={header} name="model version" />;
    },
    accessorKey: "modelVersion",
  },
  {
    id: "successful",
    accessorKey: "successful",
    header: (header) => {
      return <SearchHeaderSelect header={header} name="Successful" options={YES_OR_NO_OPTIONS} />;
    },
    cell: ({ row }) => {
      return <YesOrNoBadge successValue={true} value={row.original.successful} />;
    },
    enableSorting: false,
  },
  {
    id: "createdAt",
    header: "Created At",
    accessorKey: "createdAt",
    cell: ({ row }) => {
      return formatShortDate(row.original.createdAt);
    },
  },
];

const BAYESIA_INFERENCE_FILTER_KEYS = ["userId", "model", "modelVersion", "successful"] as const;

const BayesiaInferencesPage = () => {
  const { queryBuilder, setCurrentPage, setSorting, sortingState } = useTableQueryBuilder({
    filterKeys: BAYESIA_INFERENCE_FILTER_KEYS,
  });

  const { data: bayesiaResults, isLoading: isLoadingBayesiaResults } =
    useIntegrations().getBayesiaResults({ queryBuilder });

  return (
    <QueryBuilderContext.Provider value={{ builder: queryBuilder }}>
      <PaginatedTable
        columns={columns}
        data={bayesiaResults?.data}
        isLoadingData={isLoadingBayesiaResults}
        pagination={bayesiaResults?.pagination}
        rowLink={(bayesiaResult) => {
          return {
            to: "/integrations/bayesia/inferences/$id",
            params: { id: bayesiaResult.id },
          };
        }}
        setCurrentPage={setCurrentPage}
        setSorting={setSorting}
        sorting={sortingState}
      />
    </QueryBuilderContext.Provider>
  );
};

export const Route = createFileRoute("/_private/integrations/bayesia/inferences/")({
  component: BayesiaInferencesPage,
  validateSearch: z.object({
    page: z.number().optional(),
    ...columns.reduce(
      (acc: Record<string, z.ZodOptional<z.ZodUnion<[z.ZodString, z.ZodNumber]>>>, column) => {
        if (column.id) {
          acc[column.id] = z.string().or(z.number().int()).optional();
        }

        return acc;
      },
      {},
    ),
  }),
});
